import type { Locale } from "./types.js";
import { DEFAULT_LOCALE } from "./types.js";

/**
 * Valida si un locale es soportado
 */
export function isValidLocale(locale: string): locale is Locale {
  return ["es", "en", "fr", "pt"].includes(locale);
}

/**
 * Obtiene el locale desde headers, query params o default
 */
export function getLocaleFromRequest(
  acceptLanguage?: string,
  queryLocale?: string
): Locale {
  // Prioridad: query param > accept-language header > default
  if (queryLocale && isValidLocale(queryLocale)) {
    return queryLocale;
  }
  
  if (acceptLanguage) {
    // Parsear Accept-Language header
    const languages = acceptLanguage
      .split(",")
      .map((lang) => {
        const parts = lang.split(";");
        return parts[0]?.trim().toLowerCase().split("-")[0] ?? "";
      })
      .filter((lang) => lang.length > 0);
    
    for (const lang of languages) {
      if (isValidLocale(lang)) {
        return lang;
      }
    }
  }
  
  return DEFAULT_LOCALE;
}

/**
 * Obtiene el locale desde el navegador
 */
export function getLocaleFromBrowser(): Locale {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return DEFAULT_LOCALE;
  }
  
  const browserLang = navigator.language?.split("-")[0]?.toLowerCase() ?? "";
  return isValidLocale(browserLang) ? browserLang : DEFAULT_LOCALE;
}

/** Etiqueta BCP 47 para `Intl.NumberFormat` / `Intl.DateTimeFormat`. */
export function localeToIntlTag(locale: Locale): string {
  const map: Record<Locale, string> = {
    es: "es-ES",
    en: "en-US",
    fr: "fr-FR",
    pt: "pt-PT",
  };
  return map[locale] ?? map.es;
}

/** Valor `locale` de Open Graph (underscore). */
export function localeToOpenGraphLocale(locale: Locale): string {
  const map: Record<Locale, string> = {
    es: "es_ES",
    en: "en_US",
    fr: "fr_FR",
    pt: "pt_PT",
  };
  return map[locale] ?? map.es;
}

/** Cabecera `Accept-Language` hacia la API (idioma principal + calidad). */
export function localeToAcceptLanguageHeader(locale: Locale): string {
  switch (locale) {
    case "en":
      return "en-US,en;q=0.9";
    case "fr":
      return "fr-FR,fr;q=0.9";
    case "pt":
      return "pt-PT,pt;q=0.9";
    case "es":
    default:
      return "es-ES,es;q=0.9";
  }
}

